/**
 * LotSmith — Landing page.
 *
 * Public product site for the local-first MT5 EA + paid cloud dashboard.
 */
import { Link } from "react-router-dom";

const TIERS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    tagline: "Local EA. No cloud sync.",
    features: [
      "1 broker account",
      "All EA features locally",
      "Local risk guard",
      "Position sizer + trade manager",
    ],
    cta: "Download free",
    href: "/onboarding",
    featured: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: "$12",
    suffix: "/mo",
    yearly: "or $99/yr",
    tagline: "Cloud sync + alerts.",
    features: [
      "5 broker accounts",
      "Prop firm rule engine",
      "Telegram + Discord alerts",
      "1 year trade history",
      "Standard support",
    ],
    cta: "Start Pro",
    href: "/onboarding",
    featured: true,
  },
  {
    id: "trader",
    name: "Trader",
    price: "$29",
    suffix: "/mo",
    yearly: "or $249/yr",
    tagline: "Advanced discipline analytics.",
    features: [
      "20 broker accounts",
      "Heuristic pattern detection",
      "Weekly review email",
      "AI weekly review beta",
      "Unlimited history",
      "Priority support",
    ],
    cta: "Start Trader",
    href: "/onboarding",
    featured: false,
  },
];

export function Landing() {
  return (
    <div className="landing">
      <header className="nav">
        <div className="nav__inner">
          <Link to="/" className="nav__brand">LotSmith</Link>
          <nav className="nav__links">
            <a href="#product">Product</a>
            <Link to="/pricing">Pricing</Link>
            <a href="#how-it-works">How it works</a>
            <Link to="/login" className="btn btn--ghost">Sign in</Link>
          </nav>
        </div>
      </header>

      <section className="hero">
        <div className="hero__inner hero__inner--split">
          <div className="hero__copy">
            <div className="hero__pill">For FTMO, FundedNext &amp; 6+ prop firms</div>
            <div className="hero__eyebrow">Discipline &middot; Prop trader tool</div>
            <h1 className="hero__title">
              A risk panel for funded traders.
            </h1>
            <p className="hero__lead">
              LotSmith is a local-first MT5 Expert Advisor for manual traders. It sizes
              your trade, blocks rule-breaking setups, manages targets, and syncs the
              journal to the web when you want accountability.
            </p>
            <div className="hero__ctas">
              <Link to="/onboarding" className="btn btn--primary">Get started free</Link>
              <a href="#product" className="btn btn--ghost">See product</a>
            </div>
            <div className="hero__proof num">
              <span>MT5 only</span>
              <span>&middot;</span>
              <span>Free EA works offline</span>
              <span>&middot;</span>
              <span>Cloud optional</span>
            </div>
          </div>
          <figure className="hero__visual">
            <img src="/product-preview.png" alt="LotSmith EA panel and dashboard preview" />
          </figure>
        </div>
      </section>

      <section id="product" className="lp-section lp-product">
        <div className="lp-inner lp-product__grid">
          <div>
            <div className="section-kicker">Product</div>
            <h2>Free EA for execution. Paid cloud for accountability.</h2>
            <p>
              The free panel stays useful every trading day: risk presets, pending
              orders, split entries, break-even, trailing, three targets, daily target
              lock, and cooldown. Pro adds cloud sync, alerts, rules, journal history,
              weekly reviews, and discipline insights across accounts.
            </p>
          </div>
          <div className="lp-product__facts">
            <div><strong>1</strong><span>free offline account</span></div>
            <div><strong>5</strong><span>Pro cloud accounts</span></div>
            <div><strong>8</strong><span>rule presets</span></div>
            <div><strong>24h</strong><span>license grace before Free fallback</span></div>
          </div>
        </div>
      </section>

      <section id="pain" className="lp-section lp-pain">
        <div className="lp-inner">
          <div className="section-heading">
            <h2>The pattern that blows funded accounts</h2>
            <p>The repeatable failure modes LotSmith is built to interrupt.</p>
          </div>
          <div className="lp-pain__grid">
            <article className="lp-pain__card">
              <div className="lp-pain__icon">01</div>
              <h3>Tilting after a stop-out</h3>
              <p className="lp-pain__pain">
                You hit your daily stop, refuse to walk away, double down,
                and breach the daily-loss cap inside 20 minutes.
              </p>
              <p className="lp-pain__fix">
                <strong>LotSmith:</strong> 3 consecutive losses → 30-min cooldown.
                Buttons gray out. You can't fight yourself.
              </p>
            </article>
            <article className="lp-pain__card">
              <div className="lp-pain__icon">02</div>
              <h3>Oversized "this is the one" trade</h3>
              <p className="lp-pain__pain">
                Mental SL of "I'll close it if it goes against me." Doesn't.
                One trade = 4% account loss. Two more = breach.
              </p>
              <p className="lp-pain__fix">
                <strong>LotSmith:</strong> Lot calculator anchors on your risk %
                + SL pips. Click BUY only places the size that fits your rule.
              </p>
            </article>
            <article className="lp-pain__card">
              <div className="lp-pain__icon">03</div>
              <h3>Trading through high-impact news</h3>
              <p className="lp-pain__pain">
                NFP. CPI. FOMC. Stops slip 8-15 pips. The risk math you did
                pre-trade no longer holds.
              </p>
              <p className="lp-pain__fix">
                <strong>LotSmith:</strong> Built-in MT5 calendar guard blocks
                trades within ±2 min of red news for the relevant currency.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="lp-section lp-how">
        <div className="lp-inner">
          <div className="section-heading">
            <h2>How it works</h2>
            <p>Five-minute install. The rest is your usual MT5 workflow with guardrails.</p>
          </div>
          <ol className="lp-how__steps">
            <li>
              <div className="lp-how__num">1</div>
              <h3>Pick your prop firm</h3>
              <p>FTMO, FundedNext, MyForexFunds, The 5%ers, TopStep, E8, Funding Pips, or Personal account. Daily-loss %, total-loss %, profit target, reset timezone — auto-filled.</p>
            </li>
            <li>
              <div className="lp-how__num">2</div>
              <h3>Drop the EA into MT5</h3>
              <p>One file: <code>LotSmith.ex5</code>. Drag onto a chart, paste your device token, whitelist one URL. Panel appears top-left in 5 seconds.</p>
            </li>
            <li>
              <div className="lp-how__num">3</div>
              <h3>Trade like normal</h3>
              <p>Set Risk %, SL pips, click BUY or SELL. EA enforces position size + daily-loss + cooldown locally. Pro pushes everything to the dashboard + Telegram.</p>
            </li>
          </ol>
        </div>
      </section>

      <section className="lp-section lp-trust">
        <div className="lp-inner">
          <div className="section-heading">
            <h2>Built so you can trust it on a funded account</h2>
            <p>The boring details that matter.</p>
          </div>
          <div className="lp-trust__grid">
            <article className="lp-trust__card">
              <div className="lp-trust__icon">No broker login</div>
              <h3>Never asks for your broker password</h3>
              <p>Position sizing + risk guard run inside MT5. We never see your investor or master password. There's no API connection to your broker — only to your dashboard.</p>
            </article>
            <article className="lp-trust__card">
              <div className="lp-trust__icon">Manual execution</div>
              <h3>Not an autotrader</h3>
              <p>You click BUY / SELL yourself. The EA only sizes, sets SL/TP, and refuses entries that breach your prop firm's rule. Most firms allow this — verify your TOS first; we have a template.</p>
            </article>
            <article className="lp-trust__card">
              <div className="lp-trust__icon">Offline-first</div>
              <h3>Local-first</h3>
              <p>Free tier works fully offline. No cloud dependency. Pro pushes events to a queue file, drains over HTTP when network's up, retries on failure. Telegram + dashboard down ≠ stopped trading.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="lp-section lp-growth">
        <div className="lp-inner lp-growth__grid">
          <div>
            <div className="section-kicker">Free distribution</div>
            <h2>Useful even if a trader never pays.</h2>
            <p>
              The free EA is not a crippled trial. It is the daily trade panel.
              Revenue can come later from cloud sync, review features, affiliate
              links, broker/prop-firm referrals, and community products.
            </p>
          </div>
          <div className="lp-growth__actions">
            <a className="btn btn--primary" href="mailto:support@lotsmith.app?subject=LotSmith%20affiliate%20or%20prop%20partnership">Partner with LotSmith</a>
            <Link className="btn btn--ghost" to="/pricing">Compare tiers</Link>
          </div>
        </div>
      </section>

      <section id="faq" className="lp-section lp-faq">
        <div className="lp-inner">
          <div className="section-heading">
            <h2>FAQ</h2>
          </div>
          <dl className="lp-faq__list">
            <div>
              <dt>Do prop firms allow LotSmith?</dt>
              <dd>Most do — we don't auto-trade. Read your firm's TOS section on third-party EAs and email their support if it's ambiguous; the email template is in our docs/legal.</dd>
            </div>
            <div>
              <dt>What happens if my MT5 disconnects?</dt>
              <dd>The EA queues events locally and drains when the network comes back. Telegram + dashboard updates are best-effort, not guaranteed — read the disclaimer.</dd>
            </div>
            <div>
              <dt>MT4 support?</dt>
              <dd>MT5-only at launch. ~70% of prop traders we surveyed use MT5; we'll add MT4 once we hit 50+ paid users.</dd>
            </div>
            <div>
              <dt>Is the daily-loss enforcer client-side or server-side?</dt>
              <dd>Both. The EA blocks new trades locally so it works even when offline. Pro tier double-checks server-side via the rule engine, so a tampered EA still loses dashboard access if it tries to bypass the cap.</dd>
            </div>
            <div>
              <dt>Refunds?</dt>
              <dd>14 days, no questions. Email support@lotsmith.app from your account email within 14 days of the first paid charge. Full policy at <Link to="/refund">/refund</Link>.</dd>
            </div>
            <div>
              <dt>Can I cancel anytime?</dt>
              <dd>Yes. Cancel from <code>/app/settings → Subscription</code>. You keep access until the end of the current period.</dd>
            </div>
          </dl>
        </div>
      </section>

      <section id="pricing" className="pricing">
        <div className="pricing__inner">
          <div className="section-heading">
            <h2>Pricing</h2>
            <p>Free is actually useful. Paid unlocks cloud sync + analytics.</p>
          </div>
          <div className="pricing__grid">
            {TIERS.map((t) => (
              <article
                key={t.id}
                className={`price-card ${t.featured ? "price-card--featured" : ""}`}
              >
                {t.featured && <div className="price-card__badge">Recommended</div>}
                <div className="price-card__name">{t.name}</div>
                <div className="price-card__price num">
                  {t.price}
                  {t.suffix && <span className="price-card__suffix">{t.suffix}</span>}
                </div>
                {t.yearly && <div className="price-card__yearly">{t.yearly}</div>}
                <div className="price-card__tag">{t.tagline}</div>
                <ul className="price-card__features">
                  {t.features.map((f) => <li key={f}>{f}</li>)}
                </ul>
                <Link
                  to={t.href}
                  className={`btn ${t.featured ? "btn--primary" : "btn--ghost"} price-card__cta`}
                >
                  {t.cta}
                </Link>
              </article>
            ))}
          </div>
          <p className="pricing__team">
            Team/Educator plans (multi-trader management, API, white-label) start
            at $99/mo — <Link to="/login">contact us</Link>.
          </p>
        </div>
      </section>

      <footer className="ftr">
        <div className="ftr__inner">
          <div>&copy; 2026 LotSmith</div>
          <div className="ftr__links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/refund">Refund</Link>
            <Link to="/disclaimer">Risk disclaimer</Link>
            <Link to="/status">Status</Link>
            <a href="mailto:support@lotsmith.app">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
